// frontend/app/components/lessonPlans/LessonSarasPanel.js
//
// Collapsible wrapper around SuggestionRail for the lesson workspace only
// (S4, SME review slide 10: "Saras AI Panel shall be available in entire
// lesson plan with collapsible panel"). SuggestionRail itself is mounted at
// several other call sites across the app (chat, collections, curriculum,
// the Da Vinci kiosk) that this file does not touch and must not — collapsing
// is a lesson-workspace concern, not a rail concern, so it lives here instead
// of inside SuggestionRail.
//
// WIDTH OWNERSHIP: this component decides its own container width (the
// expanded width the caller computed, or a fixed 44px strip), so
// renderLessonPlanWorkspace never has to know whether the panel is open to
// lay the lesson content out beside it — it just renders this in the column
// it used to give SuggestionRail directly, and the lesson content reclaims
// whatever this component gives back.

import React, { useEffect, useState } from "react";
import { Platform, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import SuggestionRail from "../SuggestionRail";
import { getLessonColors } from "./lessonPlanTheme";
import { STORAGE_KEYS } from "../../constants/storageKeys";

const COLLAPSED_WIDTH = 44;

/** Read the collapsed flag left from a previous visit. Web only; never throws. */
const readStoredCollapsed = () => {
  if (Platform.OS !== "web" || typeof window === "undefined" || !window.sessionStorage) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(STORAGE_KEYS.LESSON_SARAS_PANEL_COLLAPSED) === "true";
  } catch {
    return false;
  }
};

const writeStoredCollapsed = (collapsed) => {
  if (Platform.OS !== "web" || typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEYS.LESSON_SARAS_PANEL_COLLAPSED,
      collapsed ? "true" : "false"
    );
  } catch {
    // Private-mode Safari and similar throw on write. Losing the memory of a
    // collapsed choice is not worth surfacing mid-lesson.
  }
};

/**
 * @param {number} railWidth - the expanded width the caller already computed
 *   (e.g. `lessonRailWidth` in renderLessonPlanWorkspace). Collapsed, this
 *   component reports 44 instead, so the caller's layout reclaims the rest.
 * @param {boolean} isDarkMode
 * @param {Function} t - translate function
 * @param {object} railProps - every other SuggestionRail prop, forwarded
 *   unchanged when expanded. `t`, `isDarkMode` and `customWidth` are supplied
 *   by this wrapper and should not be duplicated inside railProps.
 */
const LessonSarasPanel = ({
  railWidth,
  isDarkMode = false,
  t = (value) => value,
  railProps = {},
}) => {
  const translate = t;
  const colors = getLessonColors(isDarkMode);
  // Lazy init so the very first render already reflects whatever a prior
  // visit left behind, instead of flashing expanded for one frame.
  const [expanded, setExpanded] = useState(() => !readStoredCollapsed());

  useEffect(() => {
    writeStoredCollapsed(!expanded);
  }, [expanded]);

  const toggle = () => setExpanded((value) => !value);

  const width = expanded ? railWidth : COLLAPSED_WIDTH;

  return (
    <View
      testID="lesson-saras-panel"
      style={[
        styles.wrap,
        {
          width,
          minWidth: width,
          backgroundColor: expanded ? "transparent" : colors.railBg,
          borderWidth: expanded ? 0 : 1,
          borderColor: colors.border,
        },
      ]}
    >
      {expanded ? (
        <View style={styles.expandedBody}>
          <TouchableOpacity
            testID="lesson-saras-panel-toggle"
            onPress={toggle}
            accessibilityRole="button"
            accessibilityState={{ expanded }}
            accessibilityLabel={translate("Collapse Saras AI panel")}
            style={[
              styles.expandedToggle,
              { backgroundColor: colors.panelBg, borderColor: colors.border },
            ]}
          >
            <MaterialCommunityIcons
              name="chevron-right"
              size={16}
              color={colors.textSecondary}
            />
          </TouchableOpacity>
          <View style={styles.railFill}>
            <SuggestionRail t={t} isDarkMode={isDarkMode} {...railProps} customWidth={railWidth} />
          </View>
        </View>
      ) : (
        <TouchableOpacity
          testID="lesson-saras-panel-toggle"
          onPress={toggle}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          accessibilityLabel={translate("Expand Saras AI panel")}
          style={styles.collapsedStrip}
        >
          <MaterialCommunityIcons name="chevron-left" size={16} color={colors.textSecondary} />
          <Text style={[styles.collapsedLabel, { color: colors.textSecondary }]}>
            {translate("Saras AI")}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    flexShrink: 0,
    minHeight: 0,
    overflow: "hidden",
    borderRadius: 16,
  },
  expandedBody: { flex: 1, minHeight: 0 },
  // A small floating chip rather than a reserved header row, so the rail
  // underneath keeps the exact layout it has at every other call site.
  expandedToggle: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 5,
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  railFill: { flex: 1, minHeight: 0 },
  collapsedStrip: {
    flex: 1,
    alignItems: "center",
    justifyContent: "flex-start",
    paddingTop: 14,
    gap: 10,
  },
  collapsedLabel: {
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.5,
    // Rotated to run top-to-bottom down the 44px strip rather than wrapping
    // one letter per line.
    transform: [{ rotate: "-90deg" }],
    width: 100,
    textAlign: "center",
  },
});

export default LessonSarasPanel;
